/**
 * A BUSCA da lista de orçamentos — cliente, título, ITEM (produto/serviço) e
 * NÚMERO do documento.
 *
 * ─── AS TRÊS DECISÕES QUE QUEBRAM EM SILÊNCIO SE IGNORADAS ──────────────────
 *
 * **(a) Acento.** O app é pt-BR e o catálogo é escrito à mão: "Instalação",
 * "Manutenção", "João". Um `toLowerCase().includes()` cru — que era o que a
 * tela fazia — devolve ZERO para quem digita "instalacao" no teclado do
 * celular, e a tela parece dizer que o item não existe. A normalização dobra
 * caixa E acento (`NFD` + corte dos diacríticos), então "joao" acha "João".
 *
 * **(b) `#` é busca por NÚMERO, e ela é EXATA.** `sequence_id` é gravado como
 * inteiro e exibido com 4 casas (`#0007`), então os dois lados precisam ser
 * comparados como NÚMERO — casar a string "0007" por `includes` faria "#7"
 * trazer também #0017, #0027, #0070… e o resultado leria como busca quebrada.
 * Por isso `#7`, `#07` e `#0007` são a mesma pergunta, e todas devolvem só o 7.
 *
 * **(c) Item é ARRAY e a tela pode não ter os itens.** O casamento recebe os
 * nomes de FORA (`itemNames`) em vez de ler `quote._offline_items` por conta
 * própria: quem sabe se o índice de itens está completo é o chamador, e é ele
 * que precisa dizer isso ao usuário. Um módulo que se virasse sozinho com o
 * cache esconderia a diferença entre "não há esse produto" e "eu ainda não sei
 * quais são os produtos" — que é exatamente o corte silencioso que o repo
 * proíbe.
 */

/** Dobra caixa e acento. `'Instalação'` → `'instalacao'`. */
export function normalizeSearchText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Lê o termo como pergunta por NÚMERO do documento.
 *
 * `'#0007'` / `'#7'` / `'7'` → `7`. Qualquer coisa com letra devolve `null` —
 * inclusive `'#'` sozinho, que é o estado de quem acabou de digitar o `#` e
 * ainda não escolheu o número (tratá-lo como `0` esvaziaria a lista sem motivo).
 */
export function parseSequenceQuery(term: string): number | null {
  const cleaned = term.trim().replace(/^#/, '');
  if (!/^\d+$/.test(cleaned)) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

/** O que a busca enxerga de um documento, além dos nomes dos itens. */
export interface SearchableQuote {
  client_name?: string | null;
  title?: string | null;
  sequence_id?: number | null;
}

/**
 * `term` vazio casa com tudo (é o estado sem filtro).
 *
 * `itemNames` são os nomes dos itens DAQUELE documento — o chamador resolve de
 * onde eles vêm (ver (c) acima).
 */
export function quoteMatchesSearch(
  quote: SearchableQuote,
  term: string,
  itemNames: readonly string[] = [],
): boolean {
  const needle = normalizeSearchText(term);
  if (!needle) return true;

  // Número: pergunta EXATA, e o `#` explícito NÃO cai no texto. Quem digita
  // "#12" quer o documento 12, não todo orçamento cujo cliente tenha "12" no
  // nome — devolver os dois misturados é o que faz a busca parecer aleatória.
  const wantedSequence = parseSequenceQuery(term);
  if (wantedSequence !== null && quote.sequence_id === wantedSequence) return true;
  if (term.trim().startsWith('#')) return false;

  if (normalizeSearchText(quote.client_name).includes(needle)) return true;
  if (normalizeSearchText(quote.title).includes(needle)) return true;
  return itemNames.some((name) => normalizeSearchText(name).includes(needle));
}
